import React, { useState } from 'react';
import {
  CardBody,
  Row,
  Col,
  Card,
  Container,
  CardHeader,
  Badge,
} from 'reactstrap';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import BreadCrumb from '../Common/BreadCrumb';
import ReportTable from './ReportTable';
import ReportAdvanceSearchModal from './ReportAdvanceSearchModal';
import { downloadReport } from './reportExport';
import { EMPTY_REPORT_FILTERS, countActiveFilters } from './reportConstants';

// Shared shell for the Crude reports. They differ only in their columns and
// data source; the chrome (Advance Search popup, Excel/PDF export, pagination)
// is identical, so it lives here once.
//
// There is no quick-search box: the spec marks plain Search as N/A — keyword
// search is the `searchText` field inside the Advance Search popup.
const ReportPage = ({
  title,
  cardTitle,
  documentTitle,
  columns,
  data,
  loading = false,
  totalRows = 0,
  page = 1,
  perPage = 10,
  sortField,
  sortDirection,
  onChangePage,
  onChangeRowsPerPage,
  onSort,
  filters,
  onFiltersChange,
  endpoints,
  exportBaseName,
  emptyText,
}) => {
  const [advanceOpen, setAdvanceOpen] = useState(false);
  const [exporting, setExporting] = useState(false);

  if (documentTitle) {
    document.title = documentTitle;
  }

  const activeFilterCount = countActiveFilters(filters);

  const handleSet = next => {
    onFiltersChange(next);
    setAdvanceOpen(false);
  };

  // Export is server-generated from the SAME filters as the list — but always
  // over every matching row, not just the page on screen.
  const handleExport = async (endpoint, extension) => {
    setExporting(true);
    try {
      await downloadReport(
        endpoint,
        filters,
        `${exportBaseName}-${Date.now()}.${extension}`
      );
    } catch (error) {
      toast.error(`Failed to export data: ${error}`);
    } finally {
      setExporting(false);
    }
  };

  return (
    <React.Fragment>
      <div className='page-content'>
        <Container fluid>
          <BreadCrumb title={title} pageTitle='CRUDE' />

          <Row>
            <Col lg={12}>
              <Card>
                <CardHeader className='border-0'>
                  <div className='d-flex align-items-center flex-wrap gap-2'>
                    <h5 className='card-title mb-0 flex-grow-1'>
                      {cardTitle}
                      {activeFilterCount > 0 && (
                        <Badge color='info' className='ms-2'>
                          {activeFilterCount} filter
                          {activeFilterCount > 1 ? 's' : ''}
                        </Badge>
                      )}
                    </h5>
                    <div className='flex-shrink-0'>
                      <div className='d-flex gap-2 flex-wrap'>
                        <button
                          type='button'
                          className='btn btn-soft-secondary btn-sm'
                          onClick={() => setAdvanceOpen(true)}
                        >
                          <i className='ri-filter-3-line align-bottom me-1'></i>
                          Advance Search
                        </button>
                        <button
                          type='button'
                          className='btn btn-soft-success btn-sm'
                          disabled={exporting}
                          onClick={() =>
                            handleExport(endpoints.EXPORT_EXCEL, 'xlsx')
                          }
                        >
                          <i className='ri-file-excel-2-line align-bottom me-1'></i>
                          Export Excel
                        </button>
                        <button
                          type='button'
                          className='btn btn-soft-danger btn-sm'
                          disabled={exporting}
                          onClick={() =>
                            handleExport(endpoints.EXPORT_PDF, 'pdf')
                          }
                        >
                          <i className='ri-file-pdf-2-line align-bottom me-1'></i>
                          Export PDF
                        </button>
                      </div>
                    </div>
                  </div>
                </CardHeader>

                <CardBody className='pt-0'>
                  <ReportTable
                    columns={columns}
                    data={data}
                    loading={loading}
                    emptyText={emptyText}
                    totalRows={totalRows}
                    page={page}
                    perPage={perPage}
                    sortField={sortField}
                    sortDirection={sortDirection}
                    onChangePage={onChangePage}
                    onChangeRowsPerPage={onChangeRowsPerPage}
                    onSort={onSort}
                  />
                  <ToastContainer closeButton={false} limit={1} />
                </CardBody>
              </Card>
            </Col>
          </Row>

          <ReportAdvanceSearchModal
            isOpen={advanceOpen}
            filters={filters || EMPTY_REPORT_FILTERS}
            onSet={handleSet}
            onCancel={() => setAdvanceOpen(false)}
          />
        </Container>
      </div>
    </React.Fragment>
  );
};

export default ReportPage;
